import React, { type FunctionComponent } from "react";
import { useLegalStore } from "@stores/legal";
import "./PrivacyPolicy.scss";

export declare type PrivacyPolicyProps = {};

export const PrivacyPolicy: FunctionComponent<PrivacyPolicyProps> = ({}) => {
  const { scrolled_privacy, setScrolledPrivacy } = useLegalStore();

  const classes = () =>
    [`privacy-policy`, scrolled_privacy ? "complete" : null]
      .filter((v) => v)
      .join(" ");

  const onScroll = (evt: any) => {
    const { scrollHeight, scrollTop, clientHeight } = evt.target;
    const scrolledHeight = scrollTop + clientHeight;
    if (scrolledHeight === scrollHeight || scrolledHeight > scrollHeight - 10) {
      setScrolledPrivacy(true);
    }
  };

  return (
    <div className={classes()}>
      <section className="privacy-policy legal" onScroll={onScroll}>
        {/* <h1 className="centered">Privacy Policy</h1> */}
        <p className="dated">Effective Date Jul 29, 2024</p>
        <p>
          Bracket Labs Group, SA. (the “Company,” “we,” “us,” or “our”) has
          created this privacy policy (this “Privacy Policy”) because we know
          that you care about how information you provide to us is used and
          shared. This Privacy Policy describes the information collection and
          use practices of the Company in connection with the Bracket Products
          (including our website, our software-as-a-service platform that
          integrates with digital wallet providers, decentralized applications,
          and/or centralized and decentralized exchanges known as “Bracket”, and
          (iii) our stand-alone application known as “Bracket”) and our related
          Services.
        </p>
        <p className="bolden">
          Please note that the Company does not offer the Bracket Products or
          Services to persons or entities (i) who reside in, are located in, are
          citizens of, or incorporated in or have a registered office in the
          United States of America (“US Persons”), Panama, Iran, UK, Canada,
          North Korea, Cuba, Syria, Myanmar (Burma), Crimea, Donetsk, Luhansk,
          or any country or territory subject to country-wide or region-wide
          economic sanctions by the US “Restricted Territories”, and/or (ii) on
          lists maintained by US Office of Foreign Assets Control (OFAC) or US
          Dept. of Treasury (collectively, “Restricted Users”).
        </p>
        <p>
          By accessing and/or using the Bracket Products or Services, you are
          agreeing to the terms of this Privacy Policy and the accompanying
          Terms of Service (collectively, the “Agreement”) and you represent and
          warrant that you are not a Restricted User.
        </p>
        <p>
          Capitalized terms not defined in this Privacy Policy shall have the
          meaning set forth in our Terms of Service.
        </p>
        <h5>I. The Information We Collect and/or Receive</h5>
        <p>
          In the course of operating the Bracket Products, providing our
          Services and/or interacting with you, we will collect (and/or receive)
          the following types of information.
        </p>
        <h5>1. Contact Information</h5>
        <p>
          When you sign up to receive alerts including but not limited to,
          orders completed and claims made, you will be asked to provide certain
          information which may include your email address (collectively, the{" "}
          <b>“Contact Information”</b>).
        </p>
        <h5>2. Other Information</h5>
        <p>
          In addition to the Contact Information, we may collect or receive the
          following additional information (<b>“Other Information”</b>):
        </p>
        <ul>
          <li>
            <b>From Your Activity.</b> In an ongoing effort to improve the
            Bracket Products and the Services, we automatically collect certain
            information when you visit the Bracket Products, and/or access
            and/or use our Services. This information includes but shall not
            limited to, IP addresses, browser type and language, referring and
            exit pages and URLs, date and time, amount of time spent on
            particular pages, what sections of the Bracket Products you visit,
            and similar information concerning your use of the Bracket Products
            and Services (<b>“Activity Information”</b>).
          </li>
          <li>
            <b>From Cookies.</b>We also collect Activity Information by using
            “cookie” technology. Cookies are small packets of data that a
            website stores on the hard drive of your computer or mobile device
            to “remember” information about your visit. We may use session
            cookies (which expire once you close your web browser) and
            persistent cookies (which stay on your computer/device until you
            delete them). If you do not want us to place a cookie on your hard
            drive, you may be able to turn that feature off on your computer or
            mobile device. Please consult your Internet browser’s documentation
            for information on how to do this and how to delete persistent
            cookies. However, if you decide not to accept cookies from us, the
            Bracket Products and Services may not function properly.
          </li>
        </ul>
        <h5>3. Public Information Observed from Blockchain</h5>
        <p>
          We may collect information from your activity that is publicly visible
          and/or accessible on blockchains (
          <b>“Blockchain Activity Information”</b>). This may include blockchain
          address and information including but not limited to, the transactions
          you conduct and we may combine and/or associate this information with
          the other information we have about you.
        </p>
        <h5>4. Information Collected from Third Party Companies</h5>
        <p>
          We may receive personal information and other information about you
          from third party companies that provide wallet scanning services (
          <b>“Third Party Information”</b>). Such third party companies may
          provide us with your Wallet address and certain other information you
          choose to share with them. We will combine this Third Party
          Information with the other information we collect about you and from
          you pursuant to this Privacy Policy.
        </p>

        <h5>II. How We Use and Share Your Information</h5>
        <p>
          You authorize us to use the Contact Information, Blockchain Activity
          Information, Third Party Information, and Other Information
          (collectively, the <b>"Information"</b>) to provide our Bracket
          Products and Services to you, solicit your feedback, inform you about
          our products and services and those of our third-party marketing
          partners, and to improve our products and services.
        </p>
        <h5>Also, we may use and share the Information as described below.</h5>
        <ul>
          <li>
            <i>Agents, Providers and Related Third Parties</i>. We may engage
            other companies and individuals to perform certain business-related
            functions on our behalf. Examples may include providing technical
            assistance, order fulfillment, customer service, and marketing
            assistance. These other companies will have access to the
            Information only as necessary to perform their functions and to the
            extent permitted by law. We may also share your Information with any
            of our parent companies, subsidiaries, or other companies under
            common control with us.
          </li>
          <li>
            <i>Aggregated Information</i>. In an ongoing effort to better
            understand users of the Bracket Products and Services analyze the
            Information in aggregate form in order to operate, maintain, manage,
            and improve the Bracket Products and Services. This aggregate
            information does not identify you personally. We may use this
            aggregate information for marketing purposes. We may share this
            aggregate information with our affiliates, agents, business and
            business partners, and other third parties. We may also disclose
            aggregated user statistics in order to describe the Bracket Products
            and/or Services to current and prospective business partners and to
            other third parties for other lawful purposes
          </li>
          <li>
            <i>Business Transfers.</i> As we develop our businesses, we might
            sell or buy businesses or assets. In the event of a corporate sale,
            merger, reorganization, sale of assets, dissolution, or similar
            event, the Information may be part of the transferred assets.
          </li>
          <li>
            <i>Legal Requirements.</i> To the extent permitted by law, we may
            also disclose the Information: (i) when required by law, court
            order, or other government or law enforcement authority or
            regulatory agency; or (ii) whenever we believe that disclosing such
            Information is necessary or advisable, for example, to protect the
            rights, property, or safety of the Company or others.
          </li>
        </ul>
        <p>
          We will take reasonable measures to require that any party receiving
          any of your personal information from us undertakes to: (i) retain and
          use such information only for the purposes set out in this Privacy
          Policy; (ii) not disclose your personal information except with your
          consent, as permitted by law, or as permitted by this Privacy Policy;
          and (iii) generally protect the privacy of your personal information.
        </p>

        <h5>
          III. Accessing and Modifying Personal Information and Communication
          Preferences
        </h5>
        <p>
          You may access, review, and make changes to your personal information
          by following the instructions found on our website, the Services or by
          contacting us at{" "}
          <a href="mailto:contact@bracket.fi">contact@bracket.fi</a>. In
          addition, you may manage your receipt of marketing and
          non-transactional communications by clicking on the "unsubscribe" link
          located on the bottom of any of our marketing email. You cannot opt
          out of receiving transactional e-mails related to their account. We
          will use commercially reasonable efforts to process such requests in a
          timely manner. You should be aware, however, that it is not always
          possible to completely remove or modify information in our
          subscription databases
        </p>

        <h5>IV. Security of Your Information</h5>
        <p>
          We take commercially reasonable security measures to ensure that your
          information is treated securely and is protected from loss, misuse,
          and unauthorized access, disclosure, alteration, and destruction,
          taking into account the risks involved in processing and the nature of
          such data, and in compliance with applicable laws and regulations.
          Unfortunately, the Internet cannot be guaranteed to be 100% secure,
          and we cannot ensure or warrant the security of any information you
          provide to us. In particular, e-mail sent to or from the Bracket
          Products and/or Services may not be secure, and you should therefore
          take special care in deciding what information you send to us via
          e-mail or other electronic means. We do not accept liability for
          unintentional disclosure of your information.
        </p>

        <h5>V. Links to Third-Party Sites</h5>
        <p>
          The Bracket Products and Services may, from time to time, contain
          links to external websites. We encourage you to review the privacy and
          security policies of any externally linked websites that may be
          accessed through the Bracket Products or Services. We assume no
          responsibility or liability for the information collection and
          disclosure practices of any external websites that a user can access
          through the Bracket Products or Services. Please check the privacy
          policies of these external websites before you submit any personal
          information to them.
        </p>

        <h5>VI. Children’s Privacy</h5>
        <p>
          We do not knowingly collect, receive, maintain, or use personal
          information from children under 13 years of age, and no part of any of
          the Bracket Products and/or Services is directed to children under the
          age of 13. If you are under the age of 13, please do not provide any
          personal information through the Bracket Products and/or Services. We
          encourage parents and legal guardians to monitor their children’s
          Internet usage and to help enforce this Privacy Policy by instructing
          their children to never provide any personal information on the
          Bracket Products, Services, or any other web site without their
          permission. If you learn that your child has provided us with personal
          information without your consent, you may alert us at{" "}
          <a href="mailto:contact@bracket.fi">contact@bracket.fi</a> and we will
          endeavor to delete that information from our databases. If we learn
          that we have collected any personal information from children under
          13, we will promptly take steps to delete such information and
          terminate the child’s account.
        </p>

        <h5>VII. DO NOT TRACK</h5>
        <p>
          We do not respond to or honor “do not track” (a/k/a/ DNT) signals or
          similar mechanisms transmitted by web browsers.
        </p>

        <h5>VIII. Important Notice to All Users</h5>
        <p>
          Bracket Products and Services are not offered to Restricted Users.
          Please be aware that your information, including your personal
          information, may be transferred to, processed, maintained, and used on
          computers, servers, and systems located outside of your state,
          province, country, or other governmental jurisdiction where the
          privacy laws may not be as protective as those in your jurisdiction.
          If you are not a Restricted User and choose to use the Bracket
          Products or the Services, you do so at your own risk.
        </p>

        <h5>
          IX. Notice to Individuals located in the European Economic Area, the
          United Kingdom, or Switzerland
        </h5>
        <p>
          If you are an individual located in the European Economic Area, the
          United Kingdom or Switzerland, further information regarding the
          transfer, storage and processing of your personal data under the
          General Data Protection Regulation (2016/679) is set forth in our GDPR
          Privacy Notice below.
        </p>

        <h5>X. Changes to Our Privacy Policy and Practices.</h5>
        <p>
          This Privacy Policy is effective as of the date stated at the top of
          this Privacy Policy. We may revise this Privacy Policy from time to
          time, so review it periodically. We will post any changes to the
          Privacy Policy on the Bracket Products and/or Services, and the
          revised Privacy Policy will be effective when it is posted. By
          accessing and/or using Bracket Products and/or Services after we make
          any such changes to this Privacy Policy, you are deemed to have
          accepted such changes. Please refer back to this Privacy Policy on a
          regular basis
        </p>

        <h5>XI. Contact Us</h5>
        <p>
          If you have any questions about this Privacy Policy or to report a
          privacy issue, please contact us:
          <br />
          <a href="mailto:contact@bracket.fi">contact@bracket.fi</a>
        </p>

        <h5></h5>
      </section>
    </div>
  );
};
